<template>
  <div>
    <loading-overlay v-if="loading" />

    <title-row
      :title="page.title"
      :subtitle="page.subtitle"
      :breadcrumb-items="[
        {
          text: 'Kontrollerek',
          to: { name: 'ControllerList' },
        },
        page.breadcrumbItem,
      ]">
      <template #buttons>
        <title-row-button @click="uploadCards">
          <v-icon left>mdi-upload</v-icon>
          Kártyák feltöltése
        </title-row-button>
        <title-row-button @click="save" :loading="saving">
          <v-icon left>mdi-check</v-icon>
          Mentés
        </title-row-button>
      </template>
    </title-row>

    <v-card flat class="rounded-xl overflow-hidden">
      <v-tabs v-model="tab" align-tabs="center" color="text--primary">
        <v-tabs-slider color="primary"></v-tabs-slider>
        <v-tab key="meta"> Metaadatok </v-tab>
        <template v-if="!page.isNew">
          <v-tab key="gates"> Kapuk </v-tab>
          <v-tab key="accumulators"> Akkumulátorok </v-tab>
          <v-tab v-if="controller.enabled == 2" key="debug"> Hibakeresés </v-tab>
        </template>

        <v-tabs-items v-model="tab">
          <v-tab-item key="meta" :transition="false">
            <v-row>
              <v-col cols="12" lg="6">
                <v-card flat>
                  <v-card-title>Alapadatok</v-card-title>
                  <v-card-text>
                    <v-text-field
                      label="Megnevezés"
                      :error-messages="errors.name"
                      v-model="controller.name"
                      filled />

                    <v-text-field
                      label="Alias"
                      :error-messages="errors.alias"
                      v-model="controller.alias"
                      filled />

                    <v-text-field
                      label="Szériaszám"
                      :error-messages="errors.serial_number"
                      v-model="controller.serial_number"
                      filled />

                    <v-select
                      label="Típus"
                      :error-messages="errors.type_id"
                      v-model="controller.type_id"
                      :items="types"
                      item-text="name"
                      item-value="id"
                      filled
                      :value-comparator="$config.comparators.isEqual" />

                    <v-select
                      name="Engedélyezve"
                      v-model="controller.enabled"
                      :items="enabledSelectItems"
                      filled
                      :error-messages="enabledErrorMessage" />
                  </v-card-text>
                </v-card>
                <v-card flat>
                  <v-card-title>Lokáció</v-card-title>
                  <v-card-text>
                    <v-autocomplete
                      label="Épület"
                      :error-messages="errors.building_id"
                      v-model="controller.building_id"
                      :items="buildings"
                      item-text="name"
                      item-value="id"
                      filled
                      :value-comparator="$config.comparators.isEqual" />
                    <v-select
                      label="Épület szint"
                      :error-messages="errors.building_level_id"
                      v-model="controller.building_level_id"
                      :items="buildingLevels"
                      item-text="name"
                      item-value="id"
                      :value-comparator="$config.comparators.isEqual"
                      filled
                      :disabled="!controller.building_id" />
                    <v-text-field
                      label="Épület típus"
                      :error-messages="errors.building_type"
                      v-model="controller.building_type"
                      filled
                      placeholder="B: épület, P: parkoló, BP vagy PB: mindkettő" />

                    <v-row>
                      <v-col :cols="12" :md="6">
                        <v-masked-text-field
                          label="X koordináta"
                          :error-messages="errors.x_coord"
                          v-model="controller.x_coord"
                          filled
                          :mask="$config.inputMasks.floatNumber" />
                      </v-col>
                      <v-col :cols="12" :md="6">
                        <v-masked-text-field
                          label="Y koordináta"
                          :error-messages="errors.y_coord"
                          v-model="controller.y_coord"
                          filled
                          :mask="$config.inputMasks.floatNumber" />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" lg="6">
                <v-card flat>
                  <v-card-title>Kapcsolat</v-card-title>
                  <v-card-text>
                    <connection-form
                      v-model="controller.connection"
                      :id="controller.connection_id"
                      :errors="errors.connection" />
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item key="gates" :transition="false">
            <v-card flat>
              <v-card-title>
                Ajtók
                <v-spacer />
                <v-btn
                  color="primary"
                  depressed
                  small
                  @click="openGateEditorDialog('door')"
                  class="ma-2 rounded-xl">
                  <v-icon left>mdi-plus</v-icon>
                  Új ajtó
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-alert v-if="controller.doors.length === 0" type="info" dense>
                  Jelenleg egy ajtó sincs ehhez a kontrollerhez rendelve.
                </v-alert>
                <v-row v-else class="mb-3" dense>
                  <v-col
                    v-for="door in controller.doors"
                    :key="door.element_id"
                    cols="12"
                    md="6"
                    lg="4"
                    xl="3">
                    <gate-details
                      :gate="door"
                      :building="selectedBuilding"
                      :buildingLevel="selectedBuildingLevel"
                      :type="'door'"
                      @edit="(gate) => openGateEditorDialog('door', gate)" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-divider></v-divider>

            <v-card flat>
              <v-card-title>
                Forgóvillák
                <v-spacer />
                <v-btn
                  color="primary"
                  depressed
                  small
                  @click="openGateEditorDialog('turnstile')"
                  class="ma-2 rounded-xl">
                  <v-icon left>mdi-plus</v-icon>
                  Új forgóvilla
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-alert v-if="controller.turnstiles?.length === 0" type="info" dense>
                  Jelenleg egy forgóvilla sincs ehhez a kontrollerhez rendelve.
                </v-alert>
                <v-row v-if="controller.turnstiles?.length > 0" class="mb-3" dense>
                  <v-col
                    v-for="turnstile in controller.turnstiles"
                    :key="turnstile.element_id"
                    cols="12"
                    md="6"
                    lg="4"
                    xl="3">
                    <gate-details
                      :gate="turnstile"
                      :controller="controller"
                      :building="selectedBuilding"
                      :buildingLevel="selectedBuildingLevel"
                      :type="'turnstile'"
                      @edit="(gate) => openGateEditorDialog('turnstile', gate)" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-divider></v-divider>

            <v-card flat>
              <v-card-title>
                Sorompók
                <v-spacer />
                <v-btn
                  color="primary"
                  depressed
                  small
                  @click="openGateEditorDialog('barrier')"
                  class="ma-2 rounded-xl">
                  <v-icon left>mdi-plus</v-icon>
                  Új sorompó
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-alert v-if="controller.barriers.length === 0" type="info" dense>
                  Jelenleg egy sorompó sincs ehhez a kontrollerhez rendelve.
                </v-alert>
                <v-row v-else class="mb-3" dense>
                  <v-col
                    v-for="barrier in controller.barriers"
                    :key="barrier.element_id"
                    cols="12"
                    md="6"
                    lg="4"
                    xl="3">
                    <gate-details
                      :gate="barrier"
                      :controller="controller"
                      :building="selectedBuilding"
                      :buildingLevel="selectedBuildingLevel"
                      :type="'barrier'"
                      @edit="(gate) => openGateEditorDialog('barrier', gate)" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item key="accumulators" :transition="false">
            <v-card class="rounded-xl" flat>
              <v-card-title>
                Akkumulátorok
                <v-spacer />
                <v-btn
                  color="primary"
                  depressed
                  small
                  @click="openAccumulatorEditorDialog()"
                  class="ma-2 rounded-xl">
                  <v-icon left>mdi-plus</v-icon>
                  Új akkumulátor
                </v-btn>
              </v-card-title>
              <v-data-table
                :loading="loading"
                :headers="dataTable.headers"
                :items="this.dataTable.items"
                :options.sync="dataTable.options"
                :footer-props="$config.dataTableFooterProps"
                calculate-widths
                disable-pagination
                hide-default-footer>
                <template #[`item.active`]="{ item }">
                  <v-chip :color="item.active == 1 ? 'success' : 'error'">
                    {{ item.active == 1 ? 'Használatban' : 'Selejtezve' }}
                  </v-chip>
                </template>
                <template #[`item.installation_date`]="{ item }">
                  {{ formatDateOnly(item.installation_date) }}
                </template>
                <template #[`item.replacement_due_date`]="{ item }">
                  <div v-if="item.replacement_due_date">
                    {{ item.replacement_due_date }} év
                  </div>
                  <div v-else class="text--disabled">
                    -
                  </div>
                </template>
                <template #[`item.voltage`]="{ item }">
                  <div style="padding-left: 16px">
                    {{ item.voltage }} V
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>
          <v-tab-item v-if="controller.enabled == 2" key="debug" :transition="false">
            <v-card flat>
              <v-card-title>Hibakeresés</v-card-title>
              <v-card-text>
                <debug-info :controller="controller" />
              </v-card-text>
            </v-card>
          </v-tab-item>

        </v-tabs-items>
      </v-tabs>
    </v-card>

    <door-editor-dialog
      :key="gateEditorDialog.key"
      v-model="gateEditorDialog.isOpen"
      :gate-type="gateEditorDialog.gateType"
      :controller="controller"
      :gate="gateEditorDialog.gate"
      @save="fetchController" />

    <accumulator-dialog
      :key="`accumulator-${accumulatorDialog.key}`"
      v-model="accumulatorDialog.isOpen"
      :controllerId="controller.element_id"
      @save="fetchController" />
  </div>
</template>

<script>
import ConnectionForm from '@/components/forms/Connection.vue';
import DoorEditorDialog from './DoorEditorDialog.vue';
import GateDetails from './GateDetails.vue';
import DebugInfo from './DebugInfo.vue';
import { dataTablePage } from '@/mixins';
import AccumulatorDialog from './AccumulatorDialog.vue';

const gateObject = {
  relay: {},
  reader: {},
  building_id: null,
  building_level_id: null,
  x_coord: null,
  y_coord: null,
};

export default {
  components: {
    ConnectionForm,
    DoorEditorDialog,
    GateDetails,
    DebugInfo,
    AccumulatorDialog
  },

  mixins: [dataTablePage],

  data() {
    return {
      controller: {
        doors: [],
        barriers: [],
        turnstiles: [],
        readers: [],
        relays: [],
        accumulators: [],
        name: null,
      },
      tab: 'meta',
      relayNumber: 0,
      gateEditorDialog: {
        isOpen: false,
        gate: gateObject,
        gateType: 'door',
        key: 0,
      },
      accumulatorDialog: {
        isOpen: false,
        key: 0,
      },
      enabled_items: [
        { name: 'Letiltva', value: 0 },
        { name: 'Engedélyezve UNIOM-ban', value: 1 },
        { name: 'Engedélyezve HUNIOM-ban', value: 2 },
      ],
      building_types: [
        { name: 'Parkoló', value: 'P' },
        { name: 'Épület', value: 'B' },
        { name: 'Parkoló és Épület', value: 'BP' },
      ],
      dataTable: {
        options: {
          sortBy: ['active'],
          sortDesc: [true],
        },
        headers: [
          { text: 'Megjegyzés', value: 'description' },
          { text: 'Aktív', value: 'active' },
          { text: 'Telepítési dátum', value: 'installation_date' },
          { text: 'Beütemezett csere', value: 'replacement_due_date' },
          { text: 'Feszültség', value: 'voltage' },
        ],
        items: [],
        itemsLength: 0,
      },
      errors: {},
      loading: false,
      saving: false,
    };
  },

  mounted() {
    this.fetchController();
  },

  methods: {
    async fetchController() {
      this.loading = true;
      try {
        if (this.page.isNew) {
          return;
        }
        const response = await this.$http.get(`controllers/details/${this.$route.params.id}`);
        this.dataTable.items = response.controller.accumulators || [];
        this.dataTable.itemsLength = (response.controller.accumulators || []).length;
        this.controller.accumulators = response.controller.accumulators || [];
        this.controller = response.controller;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    async fetchControllerTypes() {
      const response = await this.$http.get('controller-types/list');
      this.types = response.types;
    },

    async uploadCards() {
      const confirm = await this.$dialog.confirm({
        type: 'warning',
        text: `Ez a parancs teljesen újraszinkronizálja a kontrollert. Ez akár 10-15 percet is igénybevehet a kártyák számától függően.<br>Folytatja?`,
        title: 'Figyelem',
      });

      if (!confirm) return;

      try {
        const response = await this.$http.post('controllers/control', {
          element_id: this.controller.element_id,
          type: 'KARTYA_FELTOLTES_KONTROLLERRE',
        });

        if (response.status === 'OK') {
          this.$dialog.notify.info('Kártyafeltöltés parancs kiadva.');
        }
      } catch (e) {
        this.$dialog.notify.error('Hiba a kártyafeltöltés során: ' + e.message);
      }
    },

    formatDateOnly(dateString) {
      if (!dateString) return '-';
      try {
        if (dateString.length === 10 && dateString.includes('-')) {
          return dateString;
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
      } catch (error) {
        return '-';
      }
    },

    async save() {
      this.saving = true;
      this.errors = {};

      this.controller.connection.name = this.controller.name;

      try {
        const response = await this.$http.post('controllers/save', this.controller);
        if (Object.keys(response.errors || {}).length) {
          return (this.errors = response.errors);
        }
        this.$dialog.notify.success('Sikeres mentés');
        if (this.page.isNew) {
          this.$router.push({ name: 'ControllerEditor', params: { id: response.controller_id } });
        }
        this.fetchController();
      } catch (e) {
        this.$dialog.notify.error(e);
      } finally {
        this.saving = false;
      }
    },

    openGateEditorDialog(type, gate = gateObject) {
      this.gateEditorDialog.isOpen = true;
      this.gateEditorDialog.gate = gate;
      this.gateEditorDialog.gateType = type;
      this.gateEditorDialog.key++;
    },

    openAccumulatorEditorDialog() {
      this.accumulatorDialog.isOpen = true;
      this.accumulatorDialog.key++;
    },
  },

  computed: {
    page() {
      switch (this.$route.name) {
        case 'NewController':
          return {
            isNew: true,
            title: 'Új kontroller',
            breadcrumbItem: {
              text: 'Új kontroller',
              disabled: true,
            },
          };
        case 'ControllerEditor':
          return {
            isNew: false,
            title: `Kontroller: ${this.controller.name ?? 'Betöltés...'}`,
            subtitle: `Kapuk: ${(this.controller.doors?.length || 0) +
              (this.controller.barriers?.length || 0) +
              (this.controller.turnstiles?.length || 0)
              }
              Olvasók: ${this.controller.readers?.length || 0}
              Relék: ${this.controller.relays?.length || 0}`,
            breadcrumbItem: {
              text: this.controller.name,
              disabled: true,
            },
          };
      }
      return {};
    },

    enabledSelectItems() {
      return [
        { text: 'Kikapcsolva', value: 0 },
        { text: 'Engedélyezve UNIOM-ban', value: 1 },
        { text: 'Engedélyezve HUNIOM-ban', value: 2 },
      ];
    },

    enabledErrorMessage() {
      if (!this.controller.enabled) {
        return 'Ezzel a kontrollerrel a beléptetőrendszer nem fog kommunikálni';
      }
      return '';
    },

    selectedBuilding() {
      return this.buildings.find((building) => building.id == this.controller.building_id);
    },

    selectedBuildingLevel() {
      return this.buildingLevels.find(
        (buildingLevel) => buildingLevel.id == this.controller.building_level_id
      );
    },
  },

  asyncComputed: {
    types: {
      async get() {
        const response = await this.$http.get('controller-types/list');
        return response.controller_types;
      },
      default: [],
    },

    buildings: {
      async get() {
        const response = await this.$http.get('buildings/list');
        return response.buildings;
      },
      default: [],
    },

    buildingLevels: {
      async get() {
        const response = await this.$http.post('building-levels/list', {
          building_id: this.controller.building_id,
        });
        return response.building_levels;
      },
      default: [],
      shouldUpdate() {
        return this.controller.building_id > 0;
      },
    },
  },
};
</script>
